import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userClaimsApi } from '../../services/api';
import { AvailableClaimDto, UserWithClaimsDto } from '../../types/UserClaims';

const UserManagementPage: React.FC = () => {
  const [availableClaims, setAvailableClaims] = useState<AvailableClaimDto[]>([]);
  const [searchEmail, setSearchEmail] = useState('');
  const [currentUser, setCurrentUser] = useState<UserWithClaimsDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [pendingClaims, setPendingClaims] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadAvailableClaims();
  }, []);

  const loadAvailableClaims = async () => {
    try {
      const claims = await userClaimsApi.getAvailableClaims();
      setAvailableClaims(claims);
    } catch (err) {
      console.error('Failed to load available claims', err);
      setError('Failed to load available claims');
    }
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchEmail.trim()) return;

    setLoading(true);
    setError(null);
    setCurrentUser(null);

    try {
      const user = await userClaimsApi.searchUserByEmail(searchEmail.trim());
      setCurrentUser(user);
      setPendingClaims(user.claims);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError(`No user found with email "${searchEmail}"`);
      } else {
        setError('Failed to search for user');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClaimToggle = (claimKey: string) => {
    setPendingClaims(prev => ({
      ...prev,
      [claimKey]: !prev[claimKey]
    }));
  };

  const handleSaveClaims = async () => {
    if (!currentUser) return;

    setSaving(true);
    try {
      const updatedUser = await userClaimsApi.setUserClaims(currentUser.uid, pendingClaims);
      setCurrentUser(updatedUser);
      setPendingClaims(updatedUser.claims);
      showNotification('Claims updated successfully. User will need to refresh their app to see changes.', 'success');
    } catch (err) {
      console.error('Failed to save claims', err);
      showNotification('Failed to update claims', 'error');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = currentUser && JSON.stringify(pendingClaims) !== JSON.stringify(currentUser.claims);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="text-emerald-600 hover:text-emerald-700 text-sm mb-2 inline-flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-1">Manage feature access for mobile app users</p>
        </div>

        {/* Notification */}
        {notification && (
          <div className={`mb-6 p-4 rounded-lg ${
            notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {notification.message}
          </div>
        )}

        {/* Search Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Find User</h2>
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1">
              <input
                type="email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                placeholder="Enter user email address..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !searchEmail.trim()}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Searching...
                </>
              ) : (
                'Search'
              )}
            </button>
          </form>
          {error && (
            <p className="mt-3 text-red-600 text-sm">{error}</p>
          )}
        </div>

        {/* User Details & Claims */}
        {currentUser && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {/* User Info */}
            <div className="border-b border-gray-200 pb-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">User Details</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium text-gray-900">{currentUser.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Display Name</p>
                  <p className="font-medium text-gray-900">{currentUser.displayName || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Firebase UID</p>
                  <p className="font-mono text-sm text-gray-600">{currentUser.uid}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email Verified</p>
                  <p className="font-medium text-gray-900">
                    {currentUser.emailVerified ? (
                      <span className="text-green-600">Yes</span>
                    ) : (
                      <span className="text-red-600">No</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Feature Claims */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Feature Access</h2>
              <div className="space-y-4">
                {availableClaims.map((claim) => (
                  <div key={claim.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{claim.name}</p>
                      <p className="text-sm text-gray-500">{claim.description}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pendingClaims[claim.key] || false}
                        onChange={() => handleClaimToggle(claim.key)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                    </label>
                  </div>
                ))}
              </div>

              {/* Save Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSaveClaims}
                  disabled={saving || !hasChanges}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>

              {/* Info Note */}
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> After updating claims, the user will need to sign out and sign back in,
                  or wait up to 1 hour for the changes to take effect in their app.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Available Claims Info (when no user selected) */}
        {!currentUser && !loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Feature Claims</h2>
            <div className="space-y-3">
              {availableClaims.map((claim) => (
                <div key={claim.key} className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">{claim.name}</p>
                  <p className="text-sm text-gray-500">{claim.description}</p>
                  <p className="text-xs text-gray-400 mt-1 font-mono">Key: {claim.key}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagementPage;
